
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { LeadBoard } from "@/components/leads/LeadBoard";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { LeadForm } from "@/components/leads/LeadForm";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/contexts/OrganizationContext";
import { Plus } from "lucide-react";

const Leads = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { currentOrganization, isLoading: orgLoading, organizations } = useOrganization();
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const checkUser = async () => {
      const { data: { session }, error } = await supabase.auth.getSession();
      if (error || !session) {
        toast({
          title: "Authentication required",
          description: "Please sign in to access this page",
          variant: "destructive",
        });
        navigate("/auth");
      }
    };
    checkUser();
  }, [navigate, toast]);

  // Debug logging for organization state
  useEffect(() => {
    console.log('📊 Leads page: Organization state', {
      currentOrganization,
      orgLoading,
      organizationsCount: organizations.length,
      hasOrganizations: organizations.length > 0
    });
  }, [currentOrganization, orgLoading, organizations]);

  // Remove duplicate real-time subscription - handled in LeadBoard component

  if (orgLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="container mx-auto py-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold">Lead Pipeline</h1>
            {currentOrganization && (
              <p className="text-muted-foreground mt-1">
                {currentOrganization.name}
              </p>
            )}
          </div>
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="outline">
                <Plus className="h-4 w-4 mr-2" />
                Add Lead
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Add New Lead</DialogTitle>
              </DialogHeader>
              <LeadForm onSuccess={() => {
                toast({
                  title: "Success",
                  description: "Lead created successfully"
                });
                setRefreshKey(prev => prev + 1);
              }} />
            </DialogContent>
          </Dialog>
        </div>
        <LeadBoard key={refreshKey} />
      </div>
    </div>
  );
};

export default Leads;
